import React, { useContext, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Store } from '../../Store';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import authService from '../../services/auth.service';

export default function ChangePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const { dispatch: ctxDispatch } = useContext(Store);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const inputStyle = {
    width: '100%',
    backgroundColor: '#F5F5F5',
    border: '1px solid #E8E8E8',
    borderRadius: '8px',
    padding: '12px 14px',
    fontSize: '14px',
    color: '#1A1A1A',
    outline: 'none',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: '#7C7C7C',
    marginBottom: '6px',
  };

  const submitHandler = async e => {
    e.preventDefault();
    if (password !== repeatPassword) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }
    setSubmitting(true);
    try {
      const data = await authService.resetPassword(token, password);
      ctxDispatch({ type: 'USER_SIGNIN', payload: data });
      localStorage.setItem('userInfo', JSON.stringify(data));
      toast.success(t('auth.userUpdatedSuccess'));
      navigate('/');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className='flex min-h-screen items-center justify-center px-4 py-10'
      style={{ backgroundColor: 'var(--background-color, #F4F4F4)' }}
    >
      <Helmet>
        <title>{t('auth.changePassword')}</title>
      </Helmet>

      <div className='w-full max-w-sm rounded-2xl shadow-lg overflow-hidden bg-white'>
        <div
          className='flex items-center px-5 py-4'
          style={{
            backgroundColor: 'var(--primaryColor)',
            color: 'var(--navFontColor)',
          }}
        >
          <button
            type='button'
            onClick={() => navigate(-1)}
            className='mr-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors'
            style={{ color: 'var(--navFontColor)' }}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className='text-base font-semibold' style={{ color: 'var(--navFontColor)' }}>
            {t('auth.changeYourPassword')}
          </h1>
        </div>

        <form onSubmit={submitHandler} className='px-6 py-6 space-y-4'>
          <p style={{ fontSize: '13px', color: '#7C7C7C', marginBottom: '4px' }}>
            {t('auth.changePasswordHint', 'Elegí una contraseña segura para tu cuenta.')}
          </p>

          <div>
            <label style={labelStyle}>{t('auth.password')}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='••••••••'
                style={{ ...inputStyle, paddingRight: '44px' }}
                autoComplete='new-password'
              />
              <button
                type='button'
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ABABAB',
                  padding: 0,
                }}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t('auth.repeatPassword')}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showRepeatPassword ? 'text' : 'password'}
                required
                value={repeatPassword}
                onChange={e => setRepeatPassword(e.target.value)}
                placeholder='••••••••'
                style={{ ...inputStyle, paddingRight: '44px' }}
                autoComplete='new-password'
              />
              <button
                type='button'
                onClick={() => setShowRepeatPassword(v => !v)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ABABAB',
                  padding: 0,
                }}
                aria-label={showRepeatPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showRepeatPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type='submit'
            disabled={submitting}
            className='w-full rounded-xl py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50'
            style={{
              backgroundColor: 'var(--secondaryColor)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
              border: 'none',
              cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: '8px',
            }}
          >
            {submitting ? t('common.loading') : t('auth.update')}
          </button>

          <p className='text-center' style={{ fontSize: '13px', color: '#7C7C7C' }}>
            <Link
              to='/signin'
              style={{
                color: 'var(--secondaryColor)',
                fontWeight: '600',
                textDecoration: 'none',
              }}
            >
              {t('auth.backToSignIn', 'Volver al inicio de sesión')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
